import { Link } from '@tanstack/react-router';
import { CheckIcon, PiggyBankIcon, PlusIcon } from 'lucide-react';
import { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { trpc } from '../../lib/trpc';

/** The group whose categories are savings pots (same rule as the API: case-insensitive) */
export const isSavingsGroup = (name: string) =>
  name.trim().toLocaleLowerCase('sv-SE') === 'sparande';

/** The household's pots: the active categories in the "Sparande" group */
export function useSavingsPots(enabled = true) {
  const groupsQuery = trpc.category.listGroups.useQuery(undefined, { enabled });
  const pots = useMemo(
    () =>
      (groupsQuery.data ?? [])
        .filter((g) => isSavingsGroup(g.name))
        .flatMap((g) => g.categories.map((c) => ({ id: c.id, name: c.name }))),
    [groupsQuery.data],
  );
  return { pots, isLoading: groupsQuery.isLoading, isError: groupsQuery.isError };
}

/**
 * "Sparande": pick the pot a transfer went to. Lists only the categories in
 * the "Sparande" group; choosing one sets that category (which also clears a
 * transfer flag, so the money counts as saved). Never creates a rule.
 */
export function SavingsPickerSheet({
  open,
  onOpenChange,
  title,
  value,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Usually the transaction's description */
  title?: string | undefined;
  /** The transaction's current category, marked when it is a pot */
  value?: string | null | undefined;
  onSelect: (pot: { id: string; name: string }) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-4 sm:max-w-sm">
        <DialogHeader className="pr-10">
          <DialogTitle className="text-[15px] leading-snug font-semibold">
            Markera som sparande
          </DialogTitle>
          <DialogDescription className="line-clamp-2">
            {title ? `${title} · ` : ''}Välj vilken sparpott pengarna gick till.
          </DialogDescription>
        </DialogHeader>
        {open && (
          <PotList
            value={value ?? null}
            onPick={(pot) => {
              onSelect(pot);
              onOpenChange(false);
            }}
            onNavigate={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PotList({
  value,
  onPick,
  onNavigate,
}: {
  value: string | null;
  onPick: (pot: { id: string; name: string }) => void;
  onNavigate: () => void;
}) {
  const { pots, isLoading, isError } = useSavingsPots();

  if (isLoading) {
    return <p className="py-4 text-center text-sm text-muted-foreground">Laddar sparpottar…</p>;
  }
  if (isError) {
    return <p className="py-4 text-center text-sm text-negative">Kunde inte ladda sparpottarna.</p>;
  }
  if (pots.length === 0) {
    return (
      <div className="space-y-3 rounded-xl bg-muted/60 p-4 text-sm">
        <p className="font-medium">Inga sparpottar än</p>
        <p className="text-muted-foreground">
          En sparpott är en kategori i gruppen Sparande, till exempel Buffert eller Barnspar.
        </p>
        <Link
          to="/settings"
          search={{ tab: 'categories' }}
          onClick={onNavigate}
          className="inline-flex min-h-11 items-center gap-1.5 font-medium text-primary hover:underline sm:min-h-0"
        >
          <PlusIcon aria-hidden className="size-4" />
          Skapa en i Kategorier
        </Link>
      </div>
    );
  }

  return (
    <ul className="-mx-1 space-y-1">
      {pots.map((pot) => {
        const selected = pot.id === value;
        return (
          <li key={pot.id}>
            <button
              type="button"
              aria-pressed={selected}
              onClick={() => onPick(pot)}
              className={cn(
                'flex min-h-12 w-full items-center gap-3 rounded-xl px-3 text-left text-base outline-none transition-colors hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50 sm:min-h-11 sm:text-sm',
                selected && 'bg-primary/10 font-medium text-primary hover:bg-primary/15',
              )}
            >
              <span
                aria-hidden
                className="flex size-8 shrink-0 items-center justify-center rounded-full bg-positive-soft text-positive"
              >
                <PiggyBankIcon className="size-4" />
              </span>
              <span className="min-w-0 flex-1 truncate">{pot.name}</span>
              {selected && <CheckIcon aria-hidden className="size-4 shrink-0" />}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
